import { supabase } from '@/integrations/supabase/client';

interface SecurityEvent {
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  userId?: string;
  userAgent?: string;
  ipAddress?: string;
  details: Record<string, any>;
  timestamp: string;
}

// Security event types
export const SECURITY_EVENTS = {
  FAILED_LOGIN: 'failed_login',
  SUSPICIOUS_REQUEST: 'suspicious_request',
  CONTENT_THREAT: 'content_threat',
  RATE_LIMIT_EXCEEDED: 'rate_limit_exceeded',
  UNAUTHORIZED_ACCESS: 'unauthorized_access',
  CSP_VIOLATION: 'csp_violation',
  XSS_ATTEMPT: 'xss_attempt',
  SQL_INJECTION_ATTEMPT: 'sql_injection_attempt'
} as const;

// Client-side security monitoring
class SecurityMonitor {
  private events: SecurityEvent[] = [];
  private maxEvents = 100;

  // Log security event
  logEvent(
    type: string, 
    severity: SecurityEvent['severity'], 
    details: Record<string, any>,
    userId?: string
  ) {
    const event: SecurityEvent = {
      type,
      severity,
      userId,
      userAgent: navigator?.userAgent,
      ipAddress: 'client', // Would be populated server-side
      details,
      timestamp: new Date().toISOString()
    };

    this.events.push(event);
    
    // Keep only recent events
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(-this.maxEvents);
    }

    // Log to console based on severity
    const logMethod = severity === 'critical' || severity === 'high' ? 'error' : 'warn';
    console[logMethod](`🚨 Security Event [${severity.toUpperCase()}]:`, type, details);

    // In production, send to monitoring service
    this.sendToMonitoringService(event);
  }

  // Send to monitoring service (placeholder)
  private async sendToMonitoringService(event: SecurityEvent) {
    try {
      // In production, this would send to your monitoring service
      // For now, we'll store in localStorage for demonstration
      const storageKey = 'security_events';
      const existingEvents = JSON.parse(localStorage.getItem(storageKey) || '[]');
      existingEvents.push(event);
      
      // Keep only last 50 events in storage
      const recentEvents = existingEvents.slice(-50);
      localStorage.setItem(storageKey, JSON.stringify(recentEvents));
      
      // Log critical events to server if needed
      if (event.severity === 'critical' || event.severity === 'high') {
        await this.logToServer(event);
      }
    } catch (error) {
      console.error('Failed to log security event:', error);
    }
  }

  // Log critical events to server
  private async logToServer(event: SecurityEvent) {
    try {
      // This would typically go to a dedicated logging table
      console.log('Would log to server:', event);
      // await supabase.from('security_logs').insert(event);
    } catch (error) {
      console.error('Failed to log to server:', error);
    }
  }

  // Get recent events for debugging
  getRecentEvents(count = 20): SecurityEvent[] {
    return this.events.slice(-count);
  }

  // Check for suspicious patterns
  detectSuspiciousActivity(): boolean {
    const recentEvents = this.getRecentEvents(10);
    const now = Date.now();
    const fiveMinutesAgo = now - (5 * 60 * 1000);

    // Check for multiple failed login attempts
    const recentFailedLogins = recentEvents.filter(event => 
      event.type === SECURITY_EVENTS.FAILED_LOGIN &&
      new Date(event.timestamp).getTime() > fiveMinutesAgo
    );

    if (recentFailedLogins.length >= 3) {
      this.logEvent(
        SECURITY_EVENTS.SUSPICIOUS_REQUEST,
        'high',
        { reason: 'Multiple failed login attempts', count: recentFailedLogins.length }
      );
      return true;
    }

    // Check for multiple content threats
    const recentContentThreats = recentEvents.filter(event =>
      event.type === SECURITY_EVENTS.CONTENT_THREAT &&
      new Date(event.timestamp).getTime() > fiveMinutesAgo
    );

    if (recentContentThreats.length >= 2) {
      this.logEvent(
        SECURITY_EVENTS.SUSPICIOUS_REQUEST,
        'high',
        { reason: 'Multiple content threats', count: recentContentThreats.length }
      );
      return true;
    }

    return false;
  }
}

// Global security monitor instance
export const securityMonitor = new SecurityMonitor();

// Helper functions for common security events
export const logFailedLogin = (email: string, reason: string) => {
  securityMonitor.logEvent(
    SECURITY_EVENTS.FAILED_LOGIN,
    'medium',
    { email, reason }
  );
};

export const logContentThreat = (contentType: string, threats: string[]) => {
  securityMonitor.logEvent(
    SECURITY_EVENTS.CONTENT_THREAT,
    'high',
    { contentType, threats }
  );
};

export const logUnauthorizedAccess = (resource: string, userId?: string) => {
  securityMonitor.logEvent(
    SECURITY_EVENTS.UNAUTHORIZED_ACCESS,
    'high',
    { resource },
    userId
  );
};

export const logRateLimitExceeded = (resource: string, userId?: string) => {
  securityMonitor.logEvent(
    SECURITY_EVENTS.RATE_LIMIT_EXCEEDED,
    'medium',
    { resource },
    userId
  );
};

export const logCSPViolation = (violation: SecurityPolicyViolationEvent) => {
  securityMonitor.logEvent(
    SECURITY_EVENTS.CSP_VIOLATION,
    'high',
    {
      violatedDirective: violation.violatedDirective,
      blockedURI: violation.blockedURI,
      sourceFile: violation.sourceFile,
      lineNumber: violation.lineNumber
    }
  );
};

// Initialize security monitoring
export const initializeSecurityMonitoring = () => {
  // Listen for CSP violations
  if (typeof window !== 'undefined') {
    window.addEventListener('securitypolicyviolation', logCSPViolation);
    
    // Monitor for suspicious activity periodically
    setInterval(() => {
      securityMonitor.detectSuspiciousActivity();
    }, 60000); // Check every minute
  }
};
